import { useCallback, useMemo } from 'react';
import { Dropdown, Form, Icon, Input, Label, SemanticCOLORS } from 'semantic-ui-react';

// Styled components
import OButton from '../../../styled/button';

// Constants
import DaysOfWeek from './days';

// Locales
import I18n from '../../../../i18n';

// Styles
import './index.scss'

interface InputValue {
    key: string,
    day: string,
    time: string
}

interface ParamsInterface {
    fieldClasses?: string,
    label?: string,
    colorLabel?: SemanticCOLORS,
    meta: {
        touched?: boolean,
        invalid?: boolean,
        error?: string
    },
    input: {
        value: InputValue[],
        onChange?: any
    },
    placeholder?: string,
    disabled?: boolean
};

const generateUniqueKey = () => Date.now().toString(36) + Math.random().toString(36).substring(2);

const CustomSchedules = (params: ParamsInterface) => {
    const availabilitySlots = useMemo(()=> params.input.value || [], [params.input.value]);

    const updateForm = useCallback((newSlots: InputValue[]) => {
        const filteredSlots = newSlots.filter(slot => slot.day && slot.time);
        params.input.onChange(filteredSlots);
    }, [params.input]);

    const handleAddSlot = useCallback(() => {
        const newSlot = {
            key: generateUniqueKey(),
            day: 'mo',
            time: '08:00',
        };
        updateForm([...availabilitySlots, newSlot]);
    }, [availabilitySlots, updateForm]);

    const handleRemoveSlot = useCallback((keyToRemove) => {
        const newSlots = availabilitySlots.filter(slot => slot.key !== keyToRemove);
        updateForm(newSlots);
    }, [availabilitySlots, updateForm]);

    const handleChangeSlot = useCallback((keyToUpdate, field, newValue) => {
        const newSlots = availabilitySlots.map(slot =>
            slot.key === keyToUpdate ? { ...slot, [field]: newValue } : slot
        );
        updateForm(newSlots);
    }, [availabilitySlots, updateForm]);

    return (
        <Form.Field className={ params.fieldClasses } error={ params.meta.touched && params.meta.invalid }>
            <div className='schedulesContainer'>
                {availabilitySlots.length === 0 ? (
                    <p>
                     {I18n.t('setup.schedules.noSchedules')}
                    </p>
                ) : (
                    // Lista de Slots de Disponibilidad
                    <div>
                        {availabilitySlots.map((slot) => (
                            <div key={slot.key} className='timeSlot'>
                                {/* Selector de Día */}
                                <Dropdown
                                    value={slot.day}
                                    className='fluid timeSlotInput'
                                    selection
                                    onChange={(e, { value }) => handleChangeSlot(slot.key, 'day', value)}
                                    options={Object.entries(DaysOfWeek).map(([key, value]) => (
                                        {
                                            key,
                                            text: value,
                                            value: key
                                        }
                                    ))}
                                />

                                {/* Selector de Tiempo */}
                                <Input
                                    type="time"
                                    className='fluid timeSlotInput'
                                    value={slot.time}
                                    onChange={(e) => handleChangeSlot(slot.key, 'time', e.target.value)}
                                    required
                                />

                                {/* Botón de Eliminar */}
                                <OButton
                                    type="button"
                                    onClick={() => handleRemoveSlot(slot.key)}
                                    aria-label="Eliminar slot de disponibilidad"
                                >
                                    {I18n.t('setup.schedules.delete')}
                                </OButton>
                            </div>
                        ))}
                    </div>
                )}

                <OButton
                    type="button"
                    onClick={handleAddSlot}
                >
                    <Icon name="add" size="small" />
                    <span>{I18n.t('setup.schedules.add')}</span>
                </OButton>
            </div>
            { params.meta.touched && params.meta.invalid && <Label basic color={ params.colorLabel || 'red' } pointing>{ params.meta.error }</Label> }
        </Form.Field>
    );

}

export default CustomSchedules;